import 'twin.macro';
/* eslint-disable camelcase */
import React from 'react';
import { v4 as uuid } from 'uuid';
import { CJEvent } from '@teetsh/app/src/types/event';
import {
  getNextDayBasedOnWorkingDays,
  getPreviousDayBasedOnWorkingDays,
} from '@teetsh/app/src/utils/time';
import addHours from '@teetsh/app/src/utils/time/addHours';
import getDay from '@teetsh/app/src/utils/time/getDay';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';
import { useCreateCJSeances } from '../../queries/useCreateCJSeances';
import Button from '@teetsh/app/src/components/Buttons/Button';
import HoverActionsWrapper from '@teetsh/app/src/components/HoverActionsWrapper';
import { Menu } from '@teetsh/app/src/components/Menu';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import {
  ArrowLeftIcon as ArrowLeft,
  ArrowRightIcon as ArrowRight,
  ArrowUpOnSquareStackIcon,
  ChevronLeftIcon as ChevronLeft,
  ChevronRightIcon as ChevronRight,
  DocumentTextIcon as DocumentText,
  Square2StackIcon as Duplicate,
  TrashIcon as Trash,
} from '@heroicons/react/20/solid';
import { ArrowPathIcon, PlusIcon as Plus } from '@heroicons/react/24/outline';
import { DuplicateIcon as DuplicateOutline } from 'components/Icons/icons/Duplicate';
import ButtonGroup from '@teetsh/app/src/components/Form/ButtonGroup';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { getMutualWorkingDays } from '@teetsh/app/src/containers/utils';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { useDeleteCJSeance } from '../../queries/useDeleteCJSeance';
import { useClassroom } from '../../DayColumn/ClassroomContext';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { logNetworkError } from '@teetsh/app/src/utils/errors';
import { useOpenCreateFicheDePrepDrawer } from '../../CreateFicheDePrepDrawer/atoms';
import { useOpenCopyEventsToAnotherDayModal } from '../../CopyEventsToAnotherDayModal/atom';
import { CopySuccessMessage } from '../../CopySuccessMessage';
import { useTranslation } from 'react-i18next';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import FakeUserProvider from 'containers/App/context/FakeUserProvider';
import { useParams } from 'react-router-dom';
import { CahierJournalView } from 'containers/CahierJournal/types';

interface Props {
  onReorder: (direction: -1 | 1) => void;
  event: CJEvent;
  isFirstEventInRow: boolean;
  isLastEventInRow: boolean;
  onRotateGroups?: () => void;
}

export function Actions(props: Props) {
  const classroom = useClassroom();
  const user = useUser();
  const { guestMode, locale } = user;
  const { school } = useSchoolSchoolyear();
  const createEvents = useCreateCJSeances();
  const openCopyEventsToAnotherDayModal = useOpenCopyEventsToAnotherDayModal();
  const removeSeance = useDeleteCJSeance();
  const {
    event,
    isLastEventInRow,
    isFirstEventInRow,
    onReorder,
    onRotateGroups,
  } = props;
  const allClassrooms = useClassrooms();
  const seanceClassrooms = event.classroomIds
    .map(clId => allClassrooms.find(c => c.id === clId))
    .filter(c => c) as Classroom[];
  const { notifySuccess } = useNotification();
  const workingDays = getMutualWorkingDays(seanceClassrooms);
  const openCreateFdpDrawer = useOpenCreateFicheDePrepDrawer();
  const { t } = useTranslation(['common', 'cahierJournal']);
  const { view } = useParams<{ view: CahierJournalView }>();

  const handleNewEvent = () => {
    const eventToCopy: CJEvent = {
      id: uuid(),
      createdAt: new Date(),
      type: 'classic',
      title: t('cahierJournal:labels.newSeance'),
      flow: '',
      bo: {
        cycle: event.bo.cycle,
        matiere: [],
        goals: [],
        domaine: [],
        competence: [],
      },
      materials: [],
      start: event.end,
      end: addHours(event.end, 1),
      customFields: [],
      attachments: [],
      classroomIds: event.classroomIds,
      status: 'open',
      order: event.order,
      tag: event.tag,
      linkedFichesDePrep: [],
      ateliers: [],
    };
    return createEvents({ cjEvents: [eventToCopy] }).catch(err => {
      logNetworkError(err, "Couldn't create new event", {
        eventId: eventToCopy.id,
      });
    });
  };

  const handleLeftCopy = () => {
    const previousDayStart = getPreviousDayBasedOnWorkingDays(
      event.start,
      workingDays,
    );

    const previousDayEnd = getPreviousDayBasedOnWorkingDays(
      event.end,
      workingDays,
    );

    const eventToCopy = {
      ...event,
      id: uuid(),
      ateliers: (event.ateliers || []).map(a => ({
        ...a,
        id: uuid(),
      })),
      start: previousDayStart,
      end: previousDayEnd,
    };
    return createEvents({ cjEvents: [eventToCopy] }).catch(err => {
      logNetworkError(err, "Couldn't create new cj event left copy", {
        eventId: eventToCopy.id,
      });
    });
  };

  const handleRightCopy = () => {
    const nextDayStart = getNextDayBasedOnWorkingDays(event.start, workingDays);
    const nextDayEnd = getNextDayBasedOnWorkingDays(event.end, workingDays);

    const eventToCopy = {
      ...event,
      ateliers: (event.ateliers || []).map(a => ({
        ...a,
        id: uuid(),
      })),
      id: uuid(),
      start: nextDayStart,
      end: nextDayEnd,
    };
    return createEvents({ cjEvents: [eventToCopy] }).catch(err => {
      logNetworkError(err, "Couldn't create new cj event right copy", {
        eventId: eventToCopy.id,
      });
    });
  };
  const { start } = event;

  const isFirstDayOfWorkingDays =
    getDay(start) === workingDays.map(w => (w + 1) % 7)[0];

  const isLastDayOfWorkingDays =
    getDay(start) === workingDays.map(w => (w + 1) % 7)[workingDays.length - 1];
  const onDuplicate = () =>
    createEvents({
      cjEvents: [
        {
          ...event,
          ateliers: (event.ateliers || []).map(a => ({
            ...a,
            id: uuid(),
          })),
          status: 'open',
          id: uuid(),
        },
      ],
    }).catch(err => {
      logNetworkError(err, "Couldn't duplicate cj event");
    });
  if (guestMode?.permission === 'read') {
    return null;
  }

  const shouldDisplayRotateGroups =
    event.ateliers &&
    event.ateliers.length > 1 &&
    event.ateliers.filter(atelier => (atelier.groupPivots || [])?.length > 0)
      .length > 1 &&
    onRotateGroups;
  return (
    <HoverActionsWrapper onClick={e => e.stopPropagation()}>
      <div role="presentation">
        <ButtonGroup tw="flex-wrap justify-end bg-neutral-supershy bg-opacity-70 border">
          {shouldDisplayRotateGroups && (
            <Tooltip2 label={t('cahierJournal:actions.rotateGroups')}>
              <Button
                minimal
                data-testid="rotate-groups"
                small
                onClick={e => {
                  e.stopPropagation();
                  onRotateGroups();
                }}
                icon={ArrowPathIcon}
              />
            </Tooltip2>
          )}
          {!isFirstEventInRow ? (
            <Tooltip2 label={t('cahierJournal:actions.moveToTheLeft')}>
              <Button
                small
                minimal
                onClick={e => {
                  e.stopPropagation();
                  onReorder(-1);
                }}
                icon={ChevronLeft}
              />
            </Tooltip2>
          ) : null}
          {!isLastEventInRow ? (
            <Tooltip2 label={t('cahierJournal:actions.moveToTheRight')}>
              <Button
                small
                minimal
                onClick={e => {
                  e.stopPropagation();
                  onReorder(1);
                }}
                icon={ChevronRight}
              />
            </Tooltip2>
          ) : null}
          {!guestMode?.enabled && (
            <Tooltip2 label={t('cahierJournal:actions.createFdpFromSeance')}>
              <Button
                minimal
                small
                onClick={e => {
                  e.stopPropagation();
                  openCreateFdpDrawer(event);
                }}
                icon={DocumentText}
              />
            </Tooltip2>
          )}
          <Tooltip2 label={t('cahierJournal:actions.addSeanceAfter')}>
            <Button
              minimal
              small
              onClick={e => {
                e.stopPropagation();
                return handleNewEvent();
              }}
              icon={Plus}
            />
          </Tooltip2>
          <Menu portal>
            <Tooltip2 label={t('cahierJournal:actions.copySeance')}>
              <Menu.Button
                minimal
                aria-label={`Copier la séance ${event.title}`}
                id="open-duplicate-menu"
                data-testid="duplicate"
                icon={Duplicate}
                small
              />
            </Tooltip2>
            <Menu.Items>
              <Menu.Item
                tw="whitespace-nowrap"
                icon={ArrowLeft}
                onClick={() => handleLeftCopy()}
                text={
                  isFirstDayOfWorkingDays
                    ? t('cahierJournal:actions.copySeanceToPreviousWeek')
                    : t('cahierJournal:actions.copySeanceToPreviousDay')
                }
              />
              <Menu.Item
                icon={ArrowRight}
                onClick={() => handleRightCopy()}
                text={
                  isLastDayOfWorkingDays
                    ? t('cahierJournal:actions.copySeanceToNextWeek')
                    : t('cahierJournal:actions.copySeanceToNextDay')
                }
              />
              <Menu.Item
                icon={DuplicateOutline as any}
                onClick={() => onDuplicate()}
                text={t('cahierJournal:actions.copySeanceAtSameTime')}
              />
              <Menu.Item
                icon={ArrowUpOnSquareStackIcon}
                onClick={() => {
                  openCopyEventsToAnotherDayModal({
                    from: event.start,
                    selectedSeances: [event],
                    classrooms: seanceClassrooms,
                    onSuccess: ({
                      destSchoolyear,
                      destSchool,
                      to,
                      eventIds,
                      newEventIds,
                    }) => {
                      notifySuccess({
                        title: t('common:messages.congratulations'),
                        layout: 'block',
                        message: (
                          <FakeUserProvider user={user}>
                            <CopySuccessMessage
                              locale={locale}
                              nbOfSpecificEventIdsSelected={eventIds}
                              newEventIds={newEventIds}
                              targetDate={to}
                              school={destSchool}
                              schoolyear={destSchoolyear}
                              view={view || 'week'}
                            />
                          </FakeUserProvider>
                        ),
                      });
                    },
                  });
                }}
                text={t('cahierJournal:actions.copyTowards')}
              />
            </Menu.Items>
          </Menu>
          <Tooltip2 label={t('cahierJournal:actions.deleteSeance')}>
            <Button
              minimal
              small
              intent="danger"
              onClick={() =>
                removeSeance({
                  seance: event,
                  classroomId:
                    school.classroomsOrg === 'unified'
                      ? undefined
                      : classroom.id,
                })
              }
              icon={Trash}
            />
          </Tooltip2>
        </ButtonGroup>
      </div>
    </HoverActionsWrapper>
  );
}
